import { motion } from 'framer-motion';
import { Target, Eye, Clock, Lock } from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'Our Mission',
    description: 'To provide exceptional legal support that empowers law firms to operate efficiently and deliver outstanding results for their clients.',
  },
  {
    icon: Eye,
    title: 'Our Vision',
    description: 'To be the most trusted legal support partner for law firms across the nation, setting the standard for accuracy and professionalism.',
  },
  {
    icon: Clock,
    title: '15+ Years',
    description: 'Of dedicated experience serving corporate law firms, immigration attorneys, and legal departments with unwavering commitment.',
  },
  {
    icon: Lock,
    title: 'Confidentiality',
    description: 'Your trust is our foundation. We maintain the highest standards of confidentiality and data security in every engagement.',
  },
];

export const AboutSection = () => {
  return (
    <section
      id="about"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #f5f5f0 0%, rgba(10,25,47,0.06) 50%, rgba(217,48,37,0.08) 100%)'
      }}
      data-testid="about-section"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 noise-texture" />
      
      {/* Gradient orbs */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-[#0a192f]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#D93025]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              About Redline Consulting
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0a192f]">
              Your Trusted Partner in Legal Excellence
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#64748b]">
              Redline Consulting is a premier legal support services firm dedicated to supporting 
              attorneys, law firms, and corporate legal departments across the United States. 
              We combine precision, efficiency, and deep legal knowledge to deliver results 
              that exceed expectations.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#64748b]">
              Our team of experienced legal professionals brings decades of combined expertise in 
              document preparation, legal research, case management, and compliance. We 
              understand the demands of modern legal practice and are committed to being 
              a reliable extension of your team.
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div className="space-y-1">
                <p className="font-heading text-3xl font-semibold text-[#0a192f]">500+</p>
                <p className="text-sm text-[#64748b]">Cases Handled</p>
              </div>
              <div className="space-y-1">
                <p className="font-heading text-3xl font-semibold text-[#0a192f]">98%</p>
                <p className="text-sm text-[#64748b]">Client Satisfaction</p>
              </div>
              <div className="space-y-1">
                <p className="font-heading text-3xl font-semibold text-[#0a192f]">24hr</p>
                <p className="text-sm text-[#64748b]">Avg. Response Time</p>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Feature Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-xl border border-[#e2e2dc] p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                data-testid={`about-feature-${index}`}
              >
                <div className="w-12 h-12 bg-[#0a192f] rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-medium text-[#0a192f] mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#64748b] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
